import React from 'react';
import { SECTOR_MAP } from '../../utils/sectors';

/** Trend icon for a sector score row. */
const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <span className="text-green-500 text-sm font-bold">↑</span>;
  if (trend === 'declining') return <span className="text-red-500 text-sm font-bold">↓</span>;
  return <span className="text-gray-400 text-sm">→</span>;
};

/** Status ring colour. */
const STATUS_RING = {
  green:   'ring-2 ring-green-400',
  amber:   'ring-2 ring-amber-400',
  red:     'ring-2 ring-red-400',
  unknown: 'ring-2 ring-gray-200',
};

const STATUS_LABEL = {
  green:   { text: 'In zone',    cls: 'bg-green-100 text-green-800' },
  amber:   { text: 'Watch this', cls: 'bg-amber-100 text-amber-800' },
  red:     { text: 'Needs care', cls: 'bg-red-100 text-red-800'     },
  unknown: { text: 'No data',    cls: 'bg-gray-100 text-gray-500'   },
};

/**
 * One sector score card — shows score, status, trend, and comfort zone bar.
 *
 * Props:
 *   sectorData  — object from /api/scores/summary: { sector, combined_score, status, trend, comfort_min, comfort_max }
 *   isPriority  — highlight if this is a member's priority sector
 */
export default function SectorScoreCard({ sectorData, isPriority = false }) {
  const meta  = SECTOR_MAP[sectorData.sector];
  const score = sectorData.combined_score;
  const label = STATUS_LABEL[sectorData.status] || STATUS_LABEL.unknown;

  /** Score bar: 0–10 scale with comfort zone overlay */
  const pct  = score != null ? (score / 10) * 100 : 0;
  const minPct = sectorData.comfort_min != null ? (sectorData.comfort_min / 10) * 100 : 40;
  const maxPct = sectorData.comfort_max != null ? (sectorData.comfort_max / 10) * 100 : 70;

  return (
    <div
      className={`
        bg-white rounded-2xl p-4 shadow-sm
        ${STATUS_RING[sectorData.status] || STATUS_RING.unknown}
        ${isPriority ? 'border-l-4 border-reset-terracotta' : ''}
        transition-all
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{meta?.emoji}</span>
          <div>
            <p className="text-sm font-semibold text-reset-brown">{meta?.label}</p>
            {isPriority && (
              <span className="text-[10px] font-medium text-reset-terracotta uppercase tracking-wide">
                Priority
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <TrendIcon trend={sectorData.trend} />
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${label.cls}`}
          >
            {label.text}
          </span>
        </div>
      </div>

      {/* Position on continuum — visual bar only, no numbers. Only drawn
          when there's real data: with status 'unknown' (no score yet, or
          hidden by the member's own privacy setting), comfort_min/max are
          null and this used to fall back to a hardcoded 40-70% green band
          anyway — which, paired with the fixed "Okay" label below, looked
          exactly like a real, positive result for a member with zero
          actual data. Confirmed 2026-09-02: Jenn saw this on Shelly Haas's
          profile after Shelly opted out of sharing — the privacy gate
          itself was working (no real scores sent), this was a purely
          decorative bar reading as real information it wasn't. */}
      {sectorData.status !== 'unknown' && (
        <div className="mt-3 mb-2">
          <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
            {/* Comfort zone band */}
            <div
              className="absolute h-full bg-green-100 rounded-full"
              style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }}
            />
            {/* Score position dot */}
            {score != null && (
              <div
                className={`absolute top-0.5 w-2 h-2 rounded-full -translate-x-1/2 ${
                  sectorData.status === 'green' ? 'bg-green-500' :
                  sectorData.status === 'red'   ? 'bg-red-500'   : 'bg-amber-500'
                }`}
                style={{ left: `${pct}%` }}
              />
            )}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
            <span>Struggling</span>
            <span className="text-center">Okay</span>
            <span>Thriving</span>
          </div>
        </div>
      )}
      {sectorData.status === 'unknown' && (
        <p className="mt-3 mb-2 text-xs text-gray-400 italic">No score yet to show.</p>
      )}
    </div>
  );
}
